import { useRef, useMemo, useEffect, useState, Suspense } from 'react';
import { Canvas } from '@react-three/fiber'
import {
  CameraControls,
  PerspectiveCamera,
  Environment,
  useProgress,
} from '@react-three/drei';
import { ChromePicker } from 'react-color'
import { VSMShadowMap} from "three";
import { Triangle } from 'react-loader-spinner'
import { debounce } from 'lodash';

import ModelCar from './assets/eg-mozy-3door_001.glb';
import hdr from './assets/portland_2k.hdr';
import groundHdr from './assets/sunset_in_the_chalk_quarry_2k.hdr';
import mozy from './assets/logo_mozy.png';

import Car from './Car';
import ClickOutside from './ClickOutside';
import IconSpray from './icons/IconSpray';
import IconClose from './icons/IconClose';
import IconSuspension from './icons/IconSuspension';
import IconWheel from './icons/IconWheel';
import IconGallery from './icons/IconGallery';

import Slider from 'react-rangeslider';
import 'react-rangeslider/lib/index.css';

import './index.css';

function LoadingScreen(props) {
  const [isHidden, setIsHidden] = useState(false);
  useEffect(() => {
    if (props.loaded) {
      setTimeout(() => {
        setIsHidden(true);
      }, 1000);
    }
  }, [props.loaded])
  return <div
    className={
      "w-full h-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 filter backdrop-blur transition-opacity delay-500 duration-500 z-40"
      + (props.loaded ? ' opacity-0' : ' opacity-100')
      + (isHidden ? ' invisible' : ' ')
    }
  >
    <Triangle
      height="80"
      width="80"
      color="#000"
      ariaLabel="triangle-loading"
      wrapperStyle={{}}
      wrapperClass="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
      visible={true}
    />
  </div>;
}

function App() {
  const camRef = useRef();
  const cameraControlsRef = useRef();

  const startDistance = useRef(0);
  const distanceCount = useRef(0);
  const endDistance = useRef(0);

  const [windowSize, setWindowSize] = useState(getWindowSize());

  const isMobileAndTabletControlVertical = useMemo(() => windowSize.innerWidth < 800 && window.matchMedia('(orientation: portrait)').matches, [windowSize.innerWidth]);
  const isMobileAndTabletControlHorizontal = useMemo(() => windowSize.innerWidth < 800 && window.matchMedia('(orientation: landscape)').matches, [windowSize.innerWidth]);

  const [carSuspension, setCarSuspension] = useState(0);
  const [carColor, setCarColor] = useState('#FFFFFF');
  const [carCarbonColor, setCarCarbonColor] = useState('#0c0c0c');
  const [caliperColor, setCaliperColor] = useState('#E7FF00');
  const [boltColor, setBoltColor] = useState('#0C0C0C');
  const [selectedPaint, setSelectedPaint] = useState(0);
  const [selectedWheelColor, setSelectedWheelColor] = useState('#FFFFFF');

  const [lipSideType, setLipSideType] = useState(1);
  const [lipSideColor, setLipSideColor] = useState('#0C0C0C');

  const [smallPartType, setSmallPartType] = useState(1);
  const [smallPartColor, setSmallPartColor] = useState('#0C0C0C');

  const [decalColor, setDecalColor] = useState('#000000');

  const [showBG, setShowBG] = useState(false);
  const [showCarSuspension, setShowCarSuspension] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showCarColorPicker, setShowCarColorPicker] = useState(false);
  const [showCaliperColorPicker, setShowCaliperColorPicker] = useState(false);
  const [showLipSideColorPicker, setShowLipSideColorPicker] = useState(false);
  const [showSmallPartColorPicker, setShowSmallPartColorPicker] = useState(false);
  const [showBoltColorPicker, setShowBoltColorPicker] = useState(false);

  const [showWheelColorPicker, setShowWheelColorPicker] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showWheel, setShowWheel] = useState(false);

  const [showLogo, setShowLogo] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const { progress, loaded } = useProgress();

  useEffect(() => {
    function handleWindowResize() {
      setWindowSize(getWindowSize());
      const vh = window.innerHeight;
      // Then we set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (progress === 100 && loaded > 0) {
      setTimeout(() => {
        setShowLogo(true)
      }, 2000);

      setTimeout(() => {
        setShowHint(true)
        setTimeout(() => {
          setShowHint(false)
        }, 10000);
      }, 3000);
    } else {
      setShowLogo(false)
    }
  }, [progress, loaded])

  function getWindowSize() {
    const { innerWidth, innerHeight } = window;
    return { innerWidth, innerHeight };
  }

  function onCameraUpdate(e) {
    if (cameraControlsRef.current) {
      if (distanceCount.current > 8) {
        startDistance.current = e.target.distance;
        distanceCount.current = 0;
      } else {
        distanceCount.current += 1;
      }
      handleOnZoom(e.target.distance);
    }
  }

  function handleCarColorChange(e) {
    setCarColor(e.hex)
  }

  function handleLipSideColorChange(e) {
    setLipSideColor(e.hex);
  }

  function handleSmallPartColorChange(e) {
    setSmallPartColor(e.hex);
  }

  function handleCaliperColorChange(e) {
    setCaliperColor(e.hex)
  }

  function handleWheelColorChange(e) {
    setSelectedWheelColor(e.hex)
  }

  function handleBoltColorChange(e) {
    setBoltColor(e.hex);
  }

  function handleSuspensionChange(val) {
    setCarSuspension(val)
  }
  function handleToggleSuspension(toggle) {
    if (toggle === 'add' && carSuspension < 0) {
      setCarSuspension(
        parseFloat(
          ((carSuspension * 100 + 1) / 100).toFixed(2)
        )
      )
    } else if (toggle === 'minus' && carSuspension > -0.04) {
      setCarSuspension(
        parseFloat(
          ((carSuspension * 100 - 1) / 100).toFixed(2)
        )
      )
    }
  }

  const handleOnZoom = (distance) => {
    if (isMobileAndTabletControlVertical
      && distance > startDistance.current
      && startDistance.current < 6
    ) {
      endDistance.current = startDistance.current;
    } else if (distance > startDistance.current) {
      endDistance.current = startDistance.current;
    }
    if (endDistance.current !== 0 && (distance > endDistance.current && distance - endDistance.current > 0.5)) {
      setZoomOut();
    }
  };

  const setZoomOut = debounce(function() {
    endDistance.current = 0;
    cameraControlsRef.current.dolly(-5, true);
    cameraControlsRef.current.setTarget(0, 0, 0, true);
  }, 500);

  function onShowCarColorPickerHandler() {
    setShowColorPicker(false);
    setShowCarColorPicker(true);
  }

  function onShowSmallPartColorPickerHandler() {
    setShowColorPicker(false);
    setShowSmallPartColorPicker(true);
  }

  function onShowLipSideColorPickerHandler() {
    setShowColorPicker(false);
    setShowLipSideColorPicker(true);
  }

  function onShowCaliperColorPickerHandler() {
    setShowWheel(false);
    setShowCaliperColorPicker(true);
  }

  function onShowCaliperBoltPickerHandler() {
    setShowWheel(false);
    setShowBoltColorPicker(true);
  }

  function onShowWheelColorPickerHandler() {
    setShowWheel(false);
    setShowWheelColorPicker(true);
  }

  function handleTriggerShowWheelPicker() {
    setShowCarColorPicker(false);
    setShowWheelColorPicker(false);
    setShowColorPicker(false);
    setShowContact(false);
    setShowCaliperColorPicker(false);
    setShowLipSideColorPicker(false);
    setShowSmallPartColorPicker(false);
    if (showWheelColorPicker) {
      setShowWheel(false)
    } else {
      setShowWheel(!showWheel)
    }
  }
  function handleCloseShowWheelPicker() {
    setShowWheelColorPicker(false);
    setShowCaliperColorPicker(false);
    setShowBoltColorPicker(false);
    setShowWheel(false);
  }

  function handleTriggerShowColorPicker() {
    setShowContact(false);
    setShowCarColorPicker(false);
    setShowCaliperColorPicker(false);
    setShowLipSideColorPicker(false);
    setShowSmallPartColorPicker(false);
    setShowBoltColorPicker(false);
    if (showCarColorPicker || showCaliperColorPicker) {
      setShowColorPicker(false)
    } else {
      setShowColorPicker(!showColorPicker)
    }
  }

  function handleCloseShowColorPicker() {
    setShowCarColorPicker(false);
    setShowLipSideColorPicker(false);
    setShowSmallPartColorPicker(false);
    setShowColorPicker(false)
  }

  function handleCloseContact() {
    setShowContact(false);
  }

  function handleTriggerShowContact() {
    setShowLipSideColorPicker(false);
    setShowSmallPartColorPicker(false);
    setShowCarColorPicker(false);
    setShowColorPicker(false);
    setShowWheel(false);
    setShowContact(!showContact)
  }


  return (
    <div
      style={{
        height: 'var(--vh)'
      }}
      className="relative"
    >
      <LoadingScreen loaded={progress >= 100} />
      <div
        className={
          "absolute p-4 text-sm top-[55px] lg:top-4 left-4 bg-white shadow-xl w-[220px] rounded-lg z-20 transition-opacity duration-300"
          + (showHint ? ' opacity-100 visible' : ' opacity-0 invisible')
        }
      >
        <div className="flex items-center justify-between mb-5">
          <h5>
            <strong>HINT 💡</strong>
          </h5>
          <button className="text-xl font-bold" onClick={() => setShowHint(false)}>&times;</button>
        </div>
        <ul className="list-disc pl-4">
          <li>Camera will be reset when zooming out</li>
          <li>Turn on BG to get better experience <IconGallery className="relative inline ml-1" /></li>
        </ul>
      </div>
      <div
        className={
          "absolute left-4 md:left-1/2 top-[25px] md:top-auto md:bottom-5 md:transform md:-translate-x-1/2 flex flex-row items-center transition-opacity duration-500"
          + (progress >= 100 && showLogo ? ' opacity-100': ' opacity-0')
        }
      >
        <img src={mozy} alt='' className="h-[11px] md:h-[17px] w-auto object-contain"/>
      </div>
      <Suspense fallback={<LoadingScreen />}>
        <Canvas
          gl={{
            antialias: true,
            toneMappingExposure: 0.6,
            pixelRatio: Math.min(window.devicePixelRatio, 2),
          }}
          shadows={VSMShadowMap}
          id="three-canvas"
          className="w-full h-full"
        >
          <Car
            isMobile={isMobileAndTabletControlVertical || isMobileAndTabletControlHorizontal}
            modelFile={ModelCar}
            suspension={carSuspension}
            color={carColor}
            carbonColor={carCarbonColor}
            selectedPaint={selectedPaint}
            boltColor={boltColor}
            caliperColor={caliperColor}
            selectedWheelColor={selectedWheelColor}
            smallPartType={smallPartType}
            smallPartColor={smallPartColor}
            lipSideType={lipSideType}
            lipSideColor={lipSideColor}
            decalColor={decalColor}
          />
          <ambientLight intensity={0.5} />
          <PerspectiveCamera
            ref={camRef}
            fov={isMobileAndTabletControlVertical ? 45 : 29.5}
            position={
              isMobileAndTabletControlVertical
              ? [-9.708949167153316, 0.8967611230370927, 6.995150145482383]
              : [-5.254570953801076, 0.7867185542086159, 7.418483454878853]
            }
            makeDefault
          />
          <Environment
              background={showBG} // Whether to affect scene.background
              files={showBG ? groundHdr : hdr}
              ground={showBG
                ? {
                  radius: 130,
                  height: 5,
                  scale: 1000
                }
                : undefined
              }
            />
          <CameraControls
            ref={cameraControlsRef}
            minPolarAngle={0}
            maxPolarAngle={Math.PI / 2.1}
            minDistance={isMobileAndTabletControlVertical ? 3.2 : 2.9}
            maxDistance={isMobileAndTabletControlVertical ? 13 : 12}
            dollyToCursor={true}
            dollySpeed={0.5}
            smoothTime={0.09}
            draggingSmoothTime ={0.15}
            truckSpeed={0}
            onChange={onCameraUpdate}
          />
        </Canvas>
      </Suspense>
      <div className="absolute z-20 bottom-16 xl:bottom-24 left-5 flex flex-col">
        <button
          className={
            "relative z-20 w-10 h-10 xl:w-16 xl:h-16 min-h-5 bg-white shadow-md rounded-full flex items-center justify-center"
            + (showBG ? '' : ' before:absolute before:top-1/2 before:left-1/2 before:transform before:-translate-x-1/2 before:-translate-y-1/2 before:-rotate-45 before:w-full before:h-px before:bg-black before-')
          }
          onClick={() => setShowBG(!showBG)}
        >
          <IconGallery className="relative right-[1px] xl:w-7 xl:h-7" />
        </button>
      </div>
      <ClickOutside func={() => setShowCarSuspension(false)}>
        <div className="absolute z-20 bottom-4 left-5 flex flex-col items-end">
          <button
            className="relative z-20 w-10 h-10 xl:w-16 xl:h-16 min-h-5 bg-white shadow-md rounded-full flex items-center justify-center"
            onClick={() => setShowCarSuspension(!showCarSuspension)}
          >
            <i className="text-[#0c0c0c]">
            {
              showCarSuspension
              ? <IconClose className="relative right-[1px] xl:w-7 xl:h-7" />
              : <IconSuspension className="relative right-[1px] xl:w-7 xl:h-7" />
            }
            </i>
          </button>
          <div
            className={
              "absolute h-[180px] z-20 left-1/2 top-[-200px] transform -translate-x-1/2 transition-opacity duration-300 ease-in-out py-2 px-5 rounded-md filter backdrop-blur shadow flex flex-col items-center" +
              (showCarSuspension ? " opacity-100" : " opacity-0 invisible pointer-none")
            }
          >
            <button
              className="text-[2rem] leading-none"
              onClick={() => handleToggleSuspension('add')}
            >
              +
            </button>
            <Slider
              min={0}
              max={0.08}
              value={carSuspension}
              tooltip={false}
              orientation='vertical'
              step={0.01}
              onChange={handleSuspensionChange}
            />
            <button
              className="text-[2rem] leading-none"
              onClick={() => handleToggleSuspension('minus')}
            >
              -
            </button>
          </div>
        </div>
      </ClickOutside>
      <ClickOutside func={handleCloseShowColorPicker}>
        <div className="absolute z-20 bottom-4 right-4 flex flex-col items-end gap-2">
          <button
            className="relative z-10 w-10 h-10 xl:w-16 xl:h-16 min-h-5 bg-white shadow rounded-full flex items-center justify-center"
            onClick={handleTriggerShowColorPicker}
          >
            <i className="text-[#0c0c0c]">
            {
              showColorPicker
                || showCarColorPicker
              ? <IconClose className="relative right-[1px] xl:w-7 xl:h-7" />
              : <IconSpray className="relative right-[1px] xl:w-7 xl:h-7" />
            }
            </i>
          </button>
          <div
            className={
              "absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out flex flex-col w-[260px] px-6 py-4 gap-4 rounded shadow bg-white" +
              (showColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
            }
          >
            <div className="relative w-full pb-4">
              <button className="w-full pb-2 flex justify-between mb-4">
                <span className="font-semibold">Car</span>
              </button>
              <ul className="flex flex-col gap-4">
                <li className="flex w-full items-center justify-between">
                  <div className="flex gap-2">
                    <button
                      className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                      style={{
                        backgroundColor: carColor
                      }}
                      onClick={onShowCarColorPickerHandler}
                    />
                    <ul className="flex items-center w-[90px]">
                      <li
                        className={
                          "rounded-l grow border p-1 text-center text-[10px] cursor-pointer" +
                          (selectedPaint === 0 ? ' text-white bg-[#0c0c0c] border-[#0c0c0c]' : ' text-[#0c0c0c] bg-white border-[#dadada]')
                        }
                        onClick={() => setSelectedPaint(0)}
                      >
                        GLOSS
                      </li>
                      <li
                        className={
                          "rounded-r grow border border-l-0 border-[#dadada] p-1 text-center text-[10px] cursor-pointer" +
                          (selectedPaint === 1 ? ' text-white bg-[#0c0c0c] border-[#0c0c0c]' : ' text-[#0c0c0c] bg-white border-[#dadada]')
                        }
                        onClick={() => setSelectedPaint(1)}
                      >
                        MATTE
                      </li>
                    </ul>
                  </div>
                  <p className="text-[14px]">Base Paint</p>
                </li>
                <li className="flex w-full items-center justify-between">
                  <div className="flex gap-2">
                    <button
                      className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                      style={{
                        backgroundColor: lipSideColor,
                        opacity: lipSideType === 0 ? 0 : 100,
                        pointerEvents: lipSideType === 0 ? 'none' : undefined,
                        visibility: lipSideType === 0 ? 'hidden' : 'visible',
                      }}
                      onClick={onShowLipSideColorPickerHandler}
                    />
                    <ul className="flex items-center w-[90px]">
                      <li
                        className={
                          "rounded-l grow border p-1 text-center text-[10px] cursor-pointer" +
                          (lipSideType === 0 ? ' text-white bg-[#0c0c0c] border-[#0c0c0c]' : ' text-[#0c0c0c] bg-white border-[#dadada]')
                        }
                        onClick={() => setLipSideType(0)}
                      >
                        Carbon
                      </li>
                      <li
                        className={
                          "rounded-r grow border border-l-0 border-[#dadada] p-1 text-center text-[10px] cursor-pointer" +
                          (lipSideType === 1 ? ' text-white bg-[#0c0c0c] border-[#0c0c0c]' : ' text-[#0c0c0c] bg-white border-[#dadada]')
                        }
                        onClick={() => setLipSideType(1)}
                      >
                        Gloss
                      </li>
                    </ul>
                  </div>
                  <p className="text-[14px]">Set 1</p>
                </li>
                <li className="flex w-full items-center justify-between">
                  <div className="flex gap-2">
                    <button
                      className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                      style={{
                        backgroundColor: smallPartColor,
                        opacity: smallPartType === 0 ? 0 : 100,
                        pointerEvents: smallPartType === 0 ? 'none' : undefined,
                        visibility: smallPartType === 0 ? 'hidden' : 'visible',
                      }}
                      onClick={onShowSmallPartColorPickerHandler}
                    />
                    <ul className="flex items-center w-[90px]">
                      <li
                        className={
                          "rounded-l grow border p-1 text-center text-[10px] cursor-pointer" +
                          (smallPartType === 0 ? ' text-white bg-[#0c0c0c] border-[#0c0c0c]' : ' text-[#0c0c0c] bg-white border-[#dadada]')
                        }
                        onClick={() => setSmallPartType(0)}
                      >
                        Carbon
                      </li>
                      <li
                        className={
                          "rounded-r grow border border-l-0 border-[#dadada] p-1 text-center text-[10px] cursor-pointer" +
                          (smallPartType === 1 ? ' text-white bg-[#0c0c0c] border-[#0c0c0c]' : ' text-[#0c0c0c] bg-white border-[#dadada]')
                        }
                        onClick={() => setSmallPartType(1)}
                      >
                        Gloss
                      </li>
                    </ul>
                  </div>
                  <p className="text-[14px]">Set 2</p>
                </li>
                <li className="flex w-full items-center justify-between">
                  <div className="flex gap-2">
                    <button
                      className={
                        "relative rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd] bg-white before:absolute before:w-full before:h-0.5 before:bg-black before:left-1/2 before:transform before:-translate-x-1/2 before:-bottom-2"
                        + (decalColor === '#FFFFFF' ? ' before:opacity-100' : ' before:opacity-0')
                      }
                      onClick={() => setDecalColor('#FFFFFF')}
                    />
                    <button
                      className={
                        "relative rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd] bg-black before:absolute before:w-full before:h-0.5 before:bg-black before:left-1/2 before:transform before:-translate-x-1/2 before:-bottom-2"
                        + (decalColor === '#000000' ? ' before:opacity-100' : ' before:opacity-0')
                      }
                      onClick={() => setDecalColor('#000000')}
                    />
                  </div>
                  <p className="text-[14px]">Decals</p>
                </li>
              </ul>
            </div>
          </div>
          {
            isMobileAndTabletControlHorizontal
              ? <>
                <div
                  className={
                    "flex gap-4 absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showCarColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <div className='flex flex-col'>
                    <p className="mb-3 font-bold">Car Base Paint</p>
                    <ChromePicker
                      color={carColor}
                      onChange={handleCarColorChange}
                    />
                  </div>
                </div>
                <div
                  className={
                    "flex gap-4 absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showLipSideColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <div className='flex flex-col'>
                    <p className="mb-3 font-bold">Set 1 Paint</p>
                    <ChromePicker
                      color={lipSideColor}
                      onChange={handleLipSideColorChange}
                    />
                  </div>
                </div>
                <div
                  className={
                    "flex gap-4 absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showSmallPartColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <div className='flex flex-col'>
                    <p className="mb-3 font-bold">Set 2 Paint</p>
                    <ChromePicker
                      color={smallPartColor}
                      onChange={handleSmallPartColorChange}
                    />
                  </div>
                </div>
              </>
              : <>
                <div
                  className={
                    "flex flex-col absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showCarColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <p className="mb-3 font-bold">Car Base Paint</p>
                  <ChromePicker
                    color={carColor}
                    onChange={handleCarColorChange}
                  />
                </div>
                <div
                  className={
                    "flex gap-4 absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showLipSideColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <div className='flex flex-col'>
                    <p className="mb-3 font-bold">Set 1 Paint</p>
                    <ChromePicker
                      color={lipSideColor}
                      onChange={handleLipSideColorChange}
                    />
                  </div>
                </div>
                <div
                  className={
                    "flex gap-4 absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showSmallPartColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <div className='flex flex-col'>
                    <p className="mb-3 font-bold">Set 2 Paint</p>
                    <ChromePicker
                      color={smallPartColor}
                      onChange={handleSmallPartColorChange}
                    />
                  </div>
                </div>
              </>
          }
        </div>
      </ClickOutside>
      <ClickOutside func={handleCloseShowWheelPicker}>
        <div className="absolute z-20 bottom-16 xl:bottom-24 right-4 flex flex-col items-end gap-2">
          <button
            className="relative z-10 w-10 h-10 xl:w-16 xl:h-16 min-h-5 bg-white shadow rounded-full flex items-center justify-center"
            onClick={handleTriggerShowWheelPicker}
          >
            <i className="text-[#0c0c0c]">
            {
              showWheel
                || showWheelColorPicker
                || showCaliperColorPicker
              ? <IconClose className="relative right-[1px] xl:w-7 xl:h-7" />
              : <IconWheel className="relative right-[1px] xl:w-7 xl:h-7" />
            }
            </i>
          </button>
          {
            isMobileAndTabletControlHorizontal
              ? <>
                <div
                  className={
                    "absolute z-20 -bottom-12 xl:-bottom-20 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out flex flex-col w-[300px] px-6 py-4 gap-4 rounded shadow bg-white" +
                    (showWheel ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <div className="relative w-full pb-4 mt-2">
                    <ul className="flex flex-col gap-4">
                      <li className="flex w-full items-center justify-between">
                        <button
                          className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                          style={{
                            backgroundColor: selectedWheelColor
                          }}
                          onClick={onShowWheelColorPickerHandler}
                        />
                        <p className="text-[14px]">Color</p>
                      </li>
                      <li className="flex w-full items-center justify-between">
                        <button
                          className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                          style={{
                            backgroundColor: caliperColor
                          }}
                          onClick={onShowCaliperColorPickerHandler}
                        />
                        <p className="text-[14px]">Caliper</p>
                      </li>
                      <li className="flex w-full items-center justify-between">
                        <button
                          className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                          style={{
                            backgroundColor: boltColor
                          }}
                          onClick={onShowCaliperBoltPickerHandler}
                        />
                        <p className="text-[14px]">Bolt</p>
                      </li>
                    </ul>
                  </div>
                </div>
                <div
                  className={
                    "flex flex-col absolute z-20 -bottom-12 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showWheelColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <p className="mb-3 font-bold">Wheel Color</p>
                  <ChromePicker
                    color={selectedWheelColor}
                    onChange={handleWheelColorChange}
                  />
                </div>
                <div
                  className={
                    "flex flex-col absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showBoltColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <p className="mb-3 font-bold">Bolt</p>
                  <ChromePicker
                    color={boltColor}
                    onChange={handleBoltColorChange}
                  />
                </div>
                <div
                  className={
                    "flex flex-col absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showCaliperColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <p className="mb-3 font-bold">Caliper</p>
                  <ChromePicker
                    color={caliperColor}
                    onChange={handleCaliperColorChange}
                  />
                </div>
              </>
              : <>
                <div
                  className={
                    "absolute z-20 -bottom-12 xl:-bottom-20 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out flex flex-col w-[220px] px-6 py-4 gap-4 rounded shadow bg-white" +
                    (showWheel ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <div className="relative w-full pb-4 mt-2">
                    <ul className="flex flex-col gap-4">
                      <li className="flex w-full items-center justify-between">
                        <button
                          className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                          style={{
                            backgroundColor: selectedWheelColor
                          }}
                          onClick={onShowWheelColorPickerHandler}
                        />
                        <p className="text-[14px]">Color</p>
                      </li>
                      <li className="flex w-full items-center justify-between">
                        <button
                          className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                          style={{
                            backgroundColor: caliperColor
                          }}
                          onClick={onShowCaliperColorPickerHandler}
                        />
                        <p className="text-[14px]">Caliper</p>
                      </li>
                      <li className="flex w-full items-center justify-between">
                        <button
                          className="rounded-full w-8 h-8 min-w-8 min-h-8 shadow border border-[#cdcdcd]"
                          style={{
                            backgroundColor: boltColor
                          }}
                          onClick={onShowCaliperBoltPickerHandler}
                        />
                        <p className="text-[14px]">Bolt</p>
                      </li>
                    </ul>
                  </div>
                </div>
                <div
                  className={
                    "flex flex-col absolute z-20 -bottom-12 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showWheelColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <p className="mb-3 font-bold">Wheel Color</p>
                  <ChromePicker
                    color={selectedWheelColor}
                    onChange={handleWheelColorChange}
                  />
                </div>
                <div
                  className={
                    "flex flex-col absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showBoltColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <p className="mb-3 font-bold">Bolt</p>
                  <ChromePicker
                    color={boltColor}
                    onChange={handleBoltColorChange}
                  />
                </div>
                <div
                  className={
                    "flex flex-col absolute z-20 bottom-0 right-full transform -translate-x-4 transition-opacity duration-300 ease-in-out bg-white p-4 shadow rounded" +
                    (showCaliperColorPicker ? " opacity-100" : " opacity-0 invisible pointer-none")
                  }
                >
                  <p className="mb-3 font-bold">Caliper</p>
                  <ChromePicker
                    color={caliperColor}
                    onChange={handleCaliperColorChange}
                  />
                </div>
              </>
          }
        </div>
      </ClickOutside>
      <ClickOutside func={handleCloseContact}>
        <div className="absolute z-20 top-3 right-4 flex flex-col items-end gap-2">
          <button
            className="relative z-10 p-2 lg:px-4 lg:py-2 min-w-[65px] bg-white shadow rounded-lg flex items-center justify-center font-bold text-sm lg:text-lg"
            onClick={handleTriggerShowContact}
          >
            { showContact ? 'Close' : 'Contact' }
          </button>
          <div
            className={
              "absolute z-20 -bottom-4 right-0 transform translate-y-full transition-opacity duration-300 ease-in-out flex flex-col w-[220px] px-6 py-4 gap-4 rounded shadow bg-white" +
              (showContact ? " opacity-100" : " opacity-0 invisible pointer-none")
            }
          >
            <div className="relative w-full">
            <div className="mb-4">
                <p className='text-gray-700 mb-2 text-sm'>Website</p>
                <a href="https://mozygarage.com/" className='hover:text-[#83c733]'>https://mozygarage.com/</a>
              </div>
              <div className="mb-4">
                <p className='text-gray-700 mb-2 text-sm'>E-Mail</p>
                <a href="mailto:tin.mozy3d@gmail.com" className='hover:text-[#83c733]'>tin.mozy3d@gmail.com</a>
              </div>
              <div className="mb-4">
                <p className='text-gray-700 mb-2 text-sm'>Instagram</p>
                <a href='https://www.instagram.com/mozy_3dcustom/' target='_blank' className='flex items-center gap-3 hover:text-[#83c733] mb-2' rel="noreferrer">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" className="w-4 h-4" strokeWidth="1.5" stroke="currentColor" fill="currentColor"><path d="M 16 3 C 8.8324839 3 3 8.8324839 3 16 L 3 34 C 3 41.167516 8.8324839 47 16 47 L 34 47 C 41.167516 47 47 41.167516 47 34 L 47 16 C 47 8.8324839 41.167516 3 34 3 L 16 3 z M 16 5 L 34 5 C 40.086484 5 45 9.9135161 45 16 L 45 34 C 45 40.086484 40.086484 45 34 45 L 16 45 C 9.9135161 45 5 40.086484 5 34 L 5 16 C 5 9.9135161 9.9135161 5 16 5 z M 37 11 A 2 2 0 0 0 35 13 A 2 2 0 0 0 37 15 A 2 2 0 0 0 39 13 A 2 2 0 0 0 37 11 z M 25 14 C 18.936712 14 14 18.936712 14 25 C 14 31.063288 18.936712 36 25 36 C 31.063288 36 36 31.063288 36 25 C 36 18.936712 31.063288 14 25 14 z M 25 16 C 29.982407 16 34 20.017593 34 25 C 34 29.982407 29.982407 34 25 34 C 20.017593 34 16 29.982407 16 25 C 16 20.017593 20.017593 16 25 16 z"></path></svg>
                  <span>mozy_3dcustom</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </ClickOutside>
    </div>
  );
}

export default App;
